import { useState } from 'react'
import { useDispatch } from 'react-redux'
import get from 'lodash/get'
import styled from 'styled-components'
import { Button, ButtonGroup, Grid, P } from '@farewill/ui'
import { COLOR, GTR } from '@farewill/ui/tokens'

import MessageBox from 'components/message-box'
import ENV from 'config/environment'
import useAdminUser from 'hooks/useAdminUser'
import { FuneralPlanLeadAttributes, Lead } from 'lib/models/lead'
import { scrollToDataId } from 'lib/window/scroll'
import { createStripePaymentLink, voidStripePaymentLink } from 'state/actions'
import { useLeadContext } from '../context'

const StyledUnderlinedButton = styled(Button.Underline)`
  padding-left: 0;
`

const StyledSecondaryButton = styled(Button.Secondary)`
  margin-right: ${GTR.S};
`

const StyledLink = styled.a`
  text-decoration: underline;
  cursor: pointer;
`

export const PaymentLinkDetails = ({
  leadUuid,
  paymentType,
  amountInPence,
  hasUnpaidPaymentLinkInvoice = false,
}: {
  leadUuid: Lead['attributes']['uuid']
  paymentType: FuneralPlanLeadAttributes['quoteAttributes']['paymentType']
  amountInPence: number
  hasUnpaidPaymentLinkInvoice?: boolean
}): React.ReactElement => {
  const adminUser = useAdminUser()
  const isEngineer = adminUser?.attributes.roles.includes(
    'farewill_engineering'
  )
  const showPrompt = ENV.FF_STRIPE_PAYMENT_LINK_ENABLED || isEngineer
  const dispatch = useDispatch()
  const { onSubmit, lead } = useLeadContext()
  const [isHavingTroubleWithPhonePayment, setIsHavingTroubleWithPhonePayment] =
    useState(false)
  const [sentEmailResult, setSentEmailResult] = useState<{
    success: boolean
    message: string
  }>()
  const [isCancellingInvoice, setIsCancellingInvoice] = useState(false)
  const [failedToCancelInvoice, setFailedToCancelInvoice] = useState(false)

  const handleCreateStripePaymentLink = async ({
    leadUuid,
    paymentType,
    amountInPence,
  }: {
    leadUuid: Lead['attributes']['uuid']
    paymentType: FuneralPlanLeadAttributes['quoteAttributes']['paymentType']
    amountInPence: number
  }): Promise<void> => {
    const response = await dispatch(
      createStripePaymentLink({
        leadUuid,
        paymentType,
        amountToPayNowInPence: amountInPence,
        product: 'funeral_plan',
      })
    )

    const result: { success: boolean; message: string } | undefined = get(
      response,
      'value.data.data.attributes.attributes'
    )
    setSentEmailResult(result)
  }

  const handleVoidStripePaymentLink = async ({
    leadUuid,
  }: {
    leadUuid: Lead['attributes']['uuid']
  }): Promise<void> => {
    const response = await dispatch(
      voidStripePaymentLink({
        leadUuid,
      })
    )

    const result: { success: boolean } | undefined = get(
      response,
      'value.data.data.attributes.attributes'
    )
    if ((result as unknown as { success: boolean }).success) {
      setIsCancellingInvoice(false)
      setSentEmailResult(undefined)
      setIsHavingTroubleWithPhonePayment(false)
    } else {
      setFailedToCancelInvoice(true)
    }
  }

  if (isCancellingInvoice) {
    return (
      <Grid data-testid="payment-link-details-cancelling-invoice">
        <Grid.Item>
          <MessageBox warning hideIcon>
            {failedToCancelInvoice ? (
              <P>
                An error occurred when trying to cancel the invoice. Try again,
                or ask Engineering for help.
              </P>
            ) : (
              <>
                <P>
                  Are you sure you want to cancel the invoice? This means the
                  customer will not be able to pay using the emailed payment
                  link.
                </P>
                <P>
                  You will be able to email another payment link later if
                  needed.
                </P>
              </>
            )}

            <span>
              <StyledSecondaryButton
                onClick={() =>
                  handleVoidStripePaymentLink({
                    leadUuid,
                  })
                }
              >
                Cancel invoice
              </StyledSecondaryButton>
              <Button.Bordered onClick={() => setIsCancellingInvoice(false)}>
                Back
              </Button.Bordered>
            </span>
          </MessageBox>
        </Grid.Item>
      </Grid>
    )
  }

  if (hasUnpaidPaymentLinkInvoice) {
    return (
      <Grid data-testid="payment-link-details-has-unpaid-invoice">
        <Grid.Item>
          <MessageBox warning hideIcon>
            <P color={COLOR.BLACK} size="L">
              Payment link sent
            </P>
            <P>
              The payment link has been sent to {lead.attributes.contact.email}.
            </P>
            <P>
              Stay on the call and click the button below once the customer has
              confirmed they have paid.
            </P>
            <ButtonGroup maxWidthInColumns={4}>
              {/* Causes the lead to refetch and allows Backstage user to get updated customer payment details  */}
              <Button.Secondary onClick={() => onSubmit}>
                Customer says they have paid
              </Button.Secondary>
              <Button.Bordered onClick={() => setIsCancellingInvoice(true)}>
                Cancel payment link invoice
              </Button.Bordered>
            </ButtonGroup>
            <P padding={[GTR.S, 0, 0]}>Need help? Contact Engineering.</P>
          </MessageBox>
        </Grid.Item>
      </Grid>
    )
  }

  if (sentEmailResult) {
    return (
      <Grid data-testid="payment-link-details-email-sent">
        <Grid.Item>
          {sentEmailResult.success ? (
            <MessageBox warning hideIcon>
              <P>{sentEmailResult.message}</P>
              {/* Causes the lead to refetch and allows Backstage user to get updated customer payment details  */}
              <Button.Secondary onClick={() => onSubmit}>
                I’ve told the customer to check their email
              </Button.Secondary>
            </MessageBox>
          ) : (
            <MessageBox error>
              <P>{sentEmailResult.message}</P>
              {/* Reset the state */}
              <Button.Secondary
                onClick={() => {
                  setSentEmailResult(undefined)
                  setIsHavingTroubleWithPhonePayment(false)
                }}
              >
                Continue
              </Button.Secondary>
            </MessageBox>
          )}
        </Grid.Item>
      </Grid>
    )
  }

  /** If !FF_STRIPE_PAYMENT_LINK_ENABLED and user is not an engineer, the option to send
   * the payment link is hidden. However, messages relating to already-issued payment links
   * will continue to be displayed (see sections above). */
  if (showPrompt) {
    return (
      <Grid data-testid="payment-link-details-send-email-option">
        <Grid.Item>
          {isHavingTroubleWithPhonePayment ? (
            <MessageBox warning hideIcon>
              <P color={COLOR.BLACK} size="L">
                Email a payment link?
              </P>
              <P>
                Email the customer a link to a Stripe payment page, where they
                can enter their card details.
              </P>

              <P>
                {lead.attributes.contact.email
                  ? `The customer email address is ${lead.attributes.contact.email}.`
                  : ''}{' '}
                <StyledLink onClick={() => scrollToDataId('contact-email')}>
                  {lead.attributes.contact.email
                    ? 'Edit email address'
                    : 'Add an email address for the customer'}
                </StyledLink>
              </P>

              <span>
                <StyledSecondaryButton
                  onClick={() =>
                    handleCreateStripePaymentLink({
                      leadUuid,
                      paymentType,
                      amountInPence,
                    })
                  }
                >
                  Send email
                </StyledSecondaryButton>
                <Button.Bordered
                  onClick={() => setIsHavingTroubleWithPhonePayment(false)}
                >
                  Cancel
                </Button.Bordered>
              </span>
            </MessageBox>
          ) : (
            <span>
              👋{' '}
              <StyledUnderlinedButton
                onClick={() => setIsHavingTroubleWithPhonePayment(true)}
              >
                Having trouble taking payment over the phone?
              </StyledUnderlinedButton>
            </span>
          )}
        </Grid.Item>
      </Grid>
    )
  } else {
    return <></>
  }
}
